import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { CustomHttpParams } from '@root/app/modules/cases/services';
import { WebSocketService } from '@root/app/modules/cases/services/websocket.service';
import { AuthService, TenantService } from '@shared/services';
import { sha256 } from 'js-sha256';
import * as aws4 from 'ngx-aws4';
import { Observable, throwError } from 'rxjs';
import { catchError, filter, finalize, first, switchMap } from 'rxjs/operators';
import { webSocket } from 'rxjs/webSocket';
import { isNullOrUndefined } from '@shared/utils/utils';
import { MessageType } from '../constants/message-type.constant';
import { NotificationMessageService } from '../services/notification-message.service';
import { MatDialog } from '@angular/material/dialog';
import { WarningDialogComponent } from '../components';
import { LoaderProxyService } from '../services/loader-proxy.service';

@Injectable()
export class AppHttpInterceptor implements HttpInterceptor {

    public credentials: any;
    constructor(
        private notificationMessageService: NotificationMessageService,
        private loaderProxy: LoaderProxyService,
        private router: Router,
        public authService: AuthService,
        private translate: TranslateService,
        private webSocketService: WebSocketService,
        private dialog: MatDialog,
        private tenantService: TenantService
    ) { }

    intercept(
        request: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {

        const invokeUrl = request.url;
        let baseUrlApi = this.tenantService.getParameter("sorrento", "baseUrlApi")
        this.loaderProxy.addItemLoading();

        if (!(invokeUrl.includes(baseUrlApi)) || invokeUrl.includes("tenantconfig") || invokeUrl.includes('bucket')) {
            let dontCatchErrors = request.params instanceof CustomHttpParams && request.params.dontCatchErrors;
            // Dont catch errors when getting an image
            if (invokeUrl.includes('/original/')){
                dontCatchErrors = true;
            }
            return next.handle(request).pipe(
                finalize(() => {
                    this.loaderProxy.removeItemLoading();
                }),
                catchError((httpError: HttpErrorResponse) => {
                    if ( dontCatchErrors){
                        return throwError(() => httpError);
                    }
                    this.notificationMessageService.openBarMessage(this.translate.instant('Error.UserError'), MessageType.ERROR);
                    this.loaderProxy.removeItemLoading();
                    return throwError(() => httpError);
                }));
        }
        else {
            // Requests are paused when credentials are being generated or refreshed
            this.authService.paused$.next(true);

            this.authService.getCredentials().then((data) => {
                this.credentials = data;
                if (isNullOrUndefined(this.webSocketService.socket$)) {
                    this.createSocketConnection()
                }
                if (this.credentials.secretAccessKey == null || this.credentials.expireTime < new Date()) {
                    this.authService.refreshCredentials().then((data) => {
                        this.credentials = data;
                    })
                }
                if (this.credentials.secretAccessKey != null) {
                    // we un pause requests
                    this.authService.paused$.next(false);
                }

            });

            return this.authService.paused$.pipe(
                filter(isPaused => !isPaused),
                first(),
                switchMap(() => this.invoke(request, next))
            );
        }

    }

    private invoke(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        let baseUrlApi = this.tenantService.getParameter("sorrento", "baseUrlApi")
        let region = this.tenantService.getParameter("cognito", "region")
        let auth;
        if (!isNullOrUndefined(request.body)) {
            auth = aws4.sign(
                {
                    body: JSON.stringify(request.body),
                    region: region,
                    method: request.method,
                    path: request.url.substring(baseUrlApi.length),
                    service: 'execute-api',
                    host: baseUrlApi.substring('https://'.length),
                    headers: {
                        'Content-Type': 'application/json',
                        'x-api-key': this.authService.getApiKey(),
                        'X-Amz-Content-Sha256': sha256(JSON.stringify(request.body)),
                        'Cache-Control': 'no-cache',
                        'Pragma': 'no-cache'
                    }
                },
                {
                    secretAccessKey: this.credentials.secretAccessKey,
                    accessKeyId: this.credentials.accessKeyId,
                    sessionToken: this.credentials.sessionToken
                })
        } else {
            auth = aws4.sign(
                {
                    region: region,
                    method: request.method,
                    path: request.url.substring(baseUrlApi.length),
                    service: 'execute-api',
                    host: baseUrlApi.substring('https://'.length),
                    headers: {
                        'x-api-key': this.authService.getApiKey(),
                        'Cache-Control': 'no-cache',
                        'Pragma': 'no-cache'
                    }
                },
                {
                    secretAccessKey: this.credentials.secretAccessKey,
                    accessKeyId: this.credentials.accessKeyId,
                    sessionToken: this.credentials.sessionToken
                })
        }


        // Setting a `host` header is being refused because it's unsafe. So let's just drop it.
        // auth.headers.delete('host');
        delete auth.headers.Host;
        // Refused to set unsafe header "Content-Length"
        delete auth.headers['Content-Length']

        request = request.clone({
            setHeaders: auth.headers
        });

        let dontCatchErrors = request.params instanceof CustomHttpParams && request.params.dontCatchErrors;
        return next.handle(request).pipe(
            finalize(() => {
                this.loaderProxy.removeItemLoading();
            }
            ),
            catchError((httpError: HttpErrorResponse) => {
                if(dontCatchErrors){
                    return throwError(() => httpError);
                }
                if (httpError.error.code === 'SORR-2') {
                    let parameters = { value: httpError.error.message.split(" ")[0] };
                    this.notificationMessageService.openBarMessage(this.translate.instant('Error.SORR2', parameters), MessageType.ERROR);
                } else if (httpError.error.code === 'SORR-4') {
                    this.notificationMessageService.openBarMessage(this.translate.instant('Error.SORR4'), MessageType.ERROR);
                } else if (httpError.error.code === 'SORR-36') {
                    this.notificationMessageService.openBarMessage(this.translate.instant('Error.SORR36'), MessageType.ERROR);
                } else {
                    this.notificationMessageService.openBarMessage(this.translate.instant('Error.UserError'), MessageType.ERROR);
                }
                this.loaderProxy.removeItemLoading();
                switch (httpError.status.toString()) {
                    case '404': {
                        this.router.navigate([`e404`]); // go to pageNotFound
                        break;
                    }
                    case '401': {
                        this.router.navigate([`e401`]); // go to 401
                        break;
                    }
                    case '500': {
                        this.router.navigate([`e500`]); // go to 500
                        break;
                    }
                    default: {
                        break;
                    }
                }
                return throwError(() => httpError);
            }));
    }

    private createSocketConnection() {

        const AWS_REGION = this.tenantService.getParameter("cognito", "region") // The region of your API-gateway
        const fullWebsocketPath = this.tenantService.getParameter('sorrento', "websocketUrl").replace('https://', ''); //https://95gx6og7b5.execute-api.eu-west-1.amazonaws.com/v1
        const SOCKET_HOST = fullWebsocketPath.split('.')[0] // Your API-gateway ID
        const ENV = fullWebsocketPath.split('/')[1] // The stage of your target deployment
        const WEBSOCKET_URL = `${SOCKET_HOST}.execute-api.${AWS_REGION}.amazonaws.com` // Don't prepend with wss!

        const signature = aws4.sign(
            {
                host: WEBSOCKET_URL,
                path: `/${ENV}?X-Amz-Security-Token=${encodeURIComponent(this.credentials.sessionToken)}`,
                service: 'execute-api',
                region: AWS_REGION,
                signQuery: true
            }, {
            accessKeyId: this.credentials.accessKeyId,
            secretAccessKey: this.credentials.secretAccessKey
        })

        // Call your webservice using your method of choice
        this.webSocketService.socket$ = webSocket(`wss://${WEBSOCKET_URL}${signature.path}`);
        this.webSocketService.socket$.subscribe(
            msg => this.webSocketService.checkReceivedMessages(msg), // Called whenever there is a message from the server.
            err => console.log(err), // Called if at any point WebSocket API signals some kind of error.
            () => this.openDialog() // Called when connection is closed (for whatever reason).
        );

        const message = {
            connectionInfo: this.router.url.split('/')[this.router.url.split('/').length - 1]
        }
        this.webSocketService.sendMessage("register", message, this.authService.getApiKey())
    }

    private openDialog() {

        let dialogRef = this.dialog.open(WarningDialogComponent, {
            width: '550px',
            data: {}
        });

        let instance = dialogRef.componentInstance;
        instance.message = this.translate.instant('Warning.SocketClosed');
        instance.btnOkText = this.translate.instant('Reload')

        dialogRef.afterClosed().subscribe(result => {
            window.location.reload();
        });

    }

}
